import React, { useEffect, useState, useRef } from 'react';

import { CircularProgress, Box, Typography, Button } from '@mui/material';
import api from '../services/api';
import { authService } from '../services/auth';

export default function PremiumDownloadPage() {
  const [percent, setPercent] = useState(0);
  const [status, setStatus] = useState('running');
  const [paymentVerified, setPaymentVerified] = useState(false);
  const downloadedRef = useRef(false);

  useEffect(() => {
    let timer;

    // First, try to verify payment from URL parameters
    async function verifyPayment() {
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session_id');
      
      if (sessionId && !paymentVerified) {
        console.log('[PremiumDownload] Verifying payment for session:', sessionId);
        try {
          const res = await api.post('/api/verify-payment', 
            { session_id: sessionId },
            { headers: { Authorization: `Bearer ${authService.getToken()}` } }
          );
          console.log('[PremiumDownload] Payment verification response:', res);
          
          if (res.success && res.status === 'upgraded') {
            console.log('[PremiumDownload] Payment verified and user upgraded!');
            setPaymentVerified(true);
          }
        } catch (error) {
          console.error('[PremiumDownload] Payment verification failed:', error);
        }
      } else {
        // No session_id, check if user is already paid and start job if needed
        console.log('[PremiumDownload] No session_id, checking if user is paid...');
        try {
          const subscriptionRes = await api.get('/api/check-subscription', {
            headers: { Authorization: `Bearer ${authService.getToken()}` },
          });
          
          if (subscriptionRes.is_paid) {
            console.log('[PremiumDownload] User is paid, starting prospect job...');
            setPaymentVerified(true);
            
            // Start prospect job for paid user
            try {
              await api.post('/api/start-prospect-job', 
                { quantity: 100 },
                { headers: { Authorization: `Bearer ${authService.getToken()}` } }
              );
              console.log('[PremiumDownload] Prospect job started for paid user');
            } catch (jobError) {
              console.error('[PremiumDownload] Failed to start prospect job:', jobError);
            }
          } else {
            console.log('[PremiumDownload] User is not paid, redirecting to plan selection');
            // User is not paid, redirect to plan selection
            window.location.href = '/choose-plan';
            return;
          }
        } catch (error) {
          console.error('[PremiumDownload] Error checking subscription:', error);
        }
      }
    }

    async function poll() {
      console.log('[PremiumDownload] Polling progress…');
      try {
        const res = await api.get('/api/prospect-download-progress', {
          headers: { Authorization: `Bearer ${authService.getToken()}` },
        });

        console.log('[PremiumDownload] Full response:', res);
        console.log('[PremiumDownload] Progress response data:', res.data);
        // Handle both direct data and nested data structure
        const responseData = res.data || res;
        const { percent: pct = 0, status: st = 'idle' } = responseData; //const { percent: pct = 0, status: st = 'idle' } = res.data || {};
        setPercent(pct);
        console.log(`[PremiumDownload] Updated percent=${pct} status=${st}`);
        setStatus(st);
        if (st !== 'done') {
          timer = setTimeout(poll, 3000);
        } else if (!downloadedRef.current) {
          downloadedRef.current = true; // ensure one-time download
          clearTimeout(timer);
          console.log('[PremiumDownload] Status done – initiating download');
          try {
            const response = await api.get('/api/download-prospects', {
              responseType: 'blob',
              headers: { Authorization: `Bearer ${authService.getToken()}` },
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            // Try to get filename from backend response header or earlier status payload
            let dlName = 'prospects.csv';
            // 1) check Content-Disposition header
            const cd = response.headers?.['content-disposition'];
            if (cd && cd.includes('filename=')) {
              dlName = cd.split('filename=')[1].replace(/"/g, '');
            } else if (responseData.filename) {
              // short-en the name client-side too (first 3 parts after 'grupa_ai_')
              const parts = responseData.filename.split('_');
              dlName = parts.slice(0, 4).join('_');
              // 2) fallback to status payload
              dlName = responseData.filename;
            }
            link.setAttribute('download', dlName);
            document.body.appendChild(link);
            link.click();
            link.remove();
           setShowManualDownload(true);
          } catch (downloadError) {
            console.error('Download failed', downloadError);
          }
        }
      } catch (err) {
        console.error('[PremiumDownload] progress poll failed', err);
        timer = setTimeout(poll, 5000);
      }
    }
    
    // Start with payment verification, then begin polling
    verifyPayment().then(() => {
      poll();
    });
    
    return () => clearTimeout(timer);
  }, [paymentVerified]);

  const [showManualDownload, setShowManualDownload] = useState(false);

  const manualDownload = async () => {
    try {
      const res = await api.get('/api/download-prospects', {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${authService.getToken()}` },
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const cd = res.headers?.['content-disposition'];
      const fname = cd && cd.includes('filename=') ? cd.split('filename=')[1].replace(/"/g, '') : 'prospects.csv';
      link.setAttribute('download', fname);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Manual download failed', err);
    }
  };

  const message = status === 'done'
    ? 'All set! Your file has downloaded and a copy was emailed to you. Feel free to continue exploring Grupa AI.'
    : 'Downloading your premium prospect list… You can leave this tab open or explore Grupa AI; a copy of the list will also arrive in your email.';

  return (
    <Box sx={{ p: 4, height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        {status !== 'done' ? (
          <>
            {/* background track */}
            <CircularProgress variant="determinate" value={100} size={220} thickness={4} sx={{ color: '#e0e0e0' }} />
            {/* actual progress */}
            <CircularProgress variant="determinate" value={percent} size={220} thickness={4} sx={{ position: 'absolute', left: 0, color: '#1E88E5' }} />
            {/* percent text */}
            <Box sx={{ top: 0, left: 0, bottom: 0, right: 0, position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="h4" component="div" color="text.primary">
                {percent}%
              </Typography>
            </Box>
          </>
        ) : (
          <Box sx={{ height: 220, width: 220, borderRadius: '50%', backgroundColor: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="fas fa-check" style={{ fontSize: 128, color: '#ffffff' }}></i>
          </Box>
        )}
      </Box>

      <Typography variant="body1" sx={{ mt: 4, maxWidth: 600 }}>
        {message}
      </Typography>

      {showManualDownload && (
        <Button variant="contained" color="primary" sx={{ mt: 3, boxShadow: 'none', textTransform: 'none' }} onClick={manualDownload}>
          Download CSV again
        </Button>
      )}
    </Box>
  );
}
